import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import InviteMemberModal from '../../components/modals/InviteMemberModal';
import teamService from '../../utils/teamService';
import authService from '../../utils/authService';
import sessionService from '../../utils/sessionService.js';
import websocketService from '../../utils/websocketService';
import { getProgressFromStatus } from '../../utils/taskProgress';

const ProjectOverview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId: urlProjectId } = useParams();
  const { user } = useAuth();
  const { switchProject } = useProject();

  // Real project data state
  const [currentProject, setCurrentProject] = useState(location.state?.project || null);
  const [loading, setLoading] = useState(!location.state?.project);
  const [error, setError] = useState(null);

  // Modal states
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [currentOrganization, setCurrentOrganization] = useState(null);

  // Load real project data
  const loadProjectData = React.useCallback(async () => {
    try {
      if (!currentProject) {
         setLoading(true);
      }

      // Get project ID from URL params, route params, or location state
      const urlParams = new URLSearchParams(location.search);
      const projectId =
        urlProjectId || urlParams.get('id') || location.state?.projectId;

      if (!projectId) {
        setError('No project ID provided');
        setLoading(false);
        return;
      }

      console.log('Loading project data for ID:', projectId);

      // Get current organization
      const currentOrg = sessionService.getCurrentOrganization();
      if (!currentOrg) {
        setError('No organization selected');
        setLoading(false);
        return;
      }

      // Import apiService dynamically to avoid circular imports
      const { default: apiService } = await import('../../utils/apiService');
      const { default: realApiService } = await import('../../utils/realApiService');

      // Load project data, stats, and team members in parallel
      const [projectData, projectStatsData, teamMembersData] = await Promise.all([
        apiService.projects.getById(projectId),
        realApiService.projects.getStats(projectId, currentOrg.id).catch(err => {
          console.warn('Failed to load project stats:', err);
          return null;
        }),
        realApiService.projects.getTeamMembers(projectId, currentOrg.id).catch(err => {
          console.warn('Failed to load project team members:', err);
          return [];
        })
      ]);

      console.log('Loaded project data:', projectData);
      console.log('Project stats loaded:', projectStatsData);
      console.log('Project team members loaded:', teamMembersData);

      // Check if project data was found
      if (!projectData) {
        console.warn('Project not found or access denied for ID:', projectId);
        setError('Project not found or you do not have access to this project');

        // Set fallback project data to show something useful
        const fallbackProject = {
          id: projectId,
          name: location.state?.project?.name || 'Project Not Found',
          description: 'This project could not be found or you do not have permission to view it.',
          status: 'unknown',
          priority: 'medium',
          startDate: new Date().toISOString().split('T')[0],
          endDate: new Date().toISOString().split('T')[0],
          progress: 0,
          progressChange: '+0% this week',
          team: { totalMembers: 0, activeMembers: 0, tasksCompleted: 0 },
        };

        setCurrentProject(fallbackProject);
        setTeamMembers([]);
        setLoading(false);
        return;
      }

      // Calculate progress dynamically
      let calculatedProgress = projectData.progress || 0;
      
      // If project is completed, force 100%
      if (projectData.status && (projectData.status.toLowerCase() === 'completed' || projectData.status.toLowerCase() === 'complete')) {
        calculatedProgress = 100;
      } else if (projectStatsData && projectStatsData.completion_rate !== undefined) {
           // Use the weighted completion rate from the backend stats API
           calculatedProgress = projectStatsData.completion_rate;
      } else if (projectStatsData && projectStatsData.task_counts) {
           // Fallback to simple calculation if completion_rate is missing
           const total = projectStatsData.task_counts.total || 0;
           const completed = projectStatsData.task_counts.completed || 0;
           if (total > 0) {
               calculatedProgress = (completed / total) * 100;
           }
      } else if (projectData.tasks) {
         // Fallback to projectData.tasks string/array if available (like in Dashboard)
         try {
          const tasks = typeof projectData.tasks === 'string' ? JSON.parse(projectData.tasks) : projectData.tasks;
          if (tasks && tasks.length > 0) {
            const progressSum = tasks.reduce((sum, task) => sum + getProgressFromStatus(task.columnName || task.status), 0);
            calculatedProgress = progressSum / tasks.length;
          }
        } catch (e) {
          console.warn('Failed to parse tasks for progress calculation:', e);
        }
      }

      // FAILSAFE: If calculated progress is 0, but we have a previous state with progress, use that
      // This handles cases where detail API lacks stats that list API had (common pattern)
      if (calculatedProgress === 0 && location.state?.project?.progress > 0) {
          console.log('Using fallback progress from navigation state:', location.state.project.progress);
          calculatedProgress = location.state.project.progress;
      }


      // Transform the data to match the expected format
      const transformedProject = {
        id: projectData.id,
        name: projectData.name || 'Untitled Project',
        description: projectData.description || 'No description available',
        status: projectData.status || 'active',
        priority: projectData.priority || 'medium',
        startDate:
          projectData.start_date || new Date().toISOString().split('T')[0],
        endDate:
          projectData.due_date || new Date().toISOString().split('T')[0],
        progress: calculatedProgress,
        progressChange: '+0% this week', // This would come from analytics

        team: {
          totalMembers: projectStatsData?.member_count || 0,
          activeMembers: teamMembersData?.length || 0,
          tasksCompleted: projectStatsData?.task_counts?.completed || 0, // Use accurate stats from /stats endpoint
        },
      };

      setCurrentProject(transformedProject);
      setTeamMembers(teamMembersData || []);
    } catch (error) {
      console.error('Failed to load project data:', error);
      setError('Failed to load project data');

      // Set fallback project data if API fails
      const fallbackProject = {
        id: 'fallback',
        name: location.state?.project?.name || 'Project',
        description: 'Unable to load project details',
        status: 'active',
        priority: 'medium',
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
        progress: 0,
        progressChange: '+0% this week',
        team: { totalMembers: 0, activeMembers: 0, tasksCompleted: 0 },
      };

      setCurrentProject(fallbackProject);
    } finally {
      setLoading(false);
    }
  }, [currentProject, location.search, location.state, urlProjectId]);

  useEffect(() => {
    loadProjectData();

    // Listen for real-time task updates to refresh progress
    const handleTaskUpdated = (data) => {
      // Check if this update belongs to our project
      // Backend uses 'project_id' or 'projectId' in payload
      const payloadProjectId = data.projectId || (data.payload && data.payload.project_id);
      
      if (payloadProjectId === (currentProject?.id || urlProjectId)) {
        console.log('⚡ WebSocket: Task update received in standalone Overview, refreshing...');
        loadProjectData();
      }
    };

    websocketService.on('taskUpdated', handleTaskUpdated);

    return () => {
      websocketService.off('taskUpdated', handleTaskUpdated);
    };
  }, [loadProjectData, currentProject?.id, urlProjectId]);

  // Load organization data
  useEffect(() => {
    const loadOrganizationData = async () => {
      try {
        const orgResponse = await authService.getCurrentOrganization();
        if (orgResponse.data && orgResponse.data.organization) {
          setCurrentOrganization(orgResponse.data.organization);
        } else {
          // Fallback organization
          setCurrentOrganization({
            id: authService.getOrganizationId() || 'fallback-org',
            name: 'Organization',
          });
        }
      } catch (error) {
        console.error('Failed to load organization:', error);
        setCurrentOrganization({
          id: 'fallback-org',
          name: 'Organization',
        });
      }
    };

    loadOrganizationData();
  }, []);

  const [userRole, setUserRole] = useState('member');
  const [activeTab, setActiveTab] = useState('overview');

  // Set active tab based on route
  useEffect(() => {
    if (location.pathname === '/tasks') {
      setActiveTab('tasks');
    } else if (location.pathname.includes('settings')) {
      setActiveTab('settings');
    } else {
      setActiveTab('overview');
    }
  }, [location.pathname]);

  // Real team members data - loaded from project stats API
  const [teamMembers, setTeamMembers] = useState([]);

  useEffect(() => {
    // Load real user role from auth context or API
    const loadUserData = async () => {
      try {
        // Import authService dynamically to avoid circular imports
        const { default: authService } = await import(
          '../../utils/authService'
        );
        const result = await authService.getCurrentUser();

        if (result.data && result.data.user) {
          const userData = result.data.user;
          // Get user role from the user data
          const role =
            userData.role ||
            userData.organizationRole ||
            sessionService.getUserRole();
          console.log('User role loaded:', role);
          setUserRole(role.toLowerCase());
        }
      } catch (error) {
        console.error('Error loading user data:', error);
        // No fallback - let role be null if not found
        setUserRole(sessionService.getUserRole());
      }
    };

    loadUserData();
  }, []);

  const tabs = [
    {
      id: 'overview',
      label: 'Overview',
      icon: 'BarChart3',
      description: 'Project summary and key metrics',
      roles: ['member', 'admin', 'owner'],
    },
    {
      id: 'tasks',
      label: 'Tasks',
      icon: 'CheckSquare',
      description: 'Task management and tracking',
      roles: ['member', 'admin', 'owner'],
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: 'Settings',
      description: 'Project configuration and permissions',
      roles: ['admin', 'owner'],
    },
  ];

  const visibleTabs = tabs.filter((tab) => tab.roles.includes(userRole));

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const handleGoToBoard = async () => {
    if (!currentProject) return;

    try {
      await switchProject(currentProject.id, {
        navigate: true,
        navigateTo: '/kanban-board'
      });
    } catch (error) {
      console.error('Failed to switch project:', error);
      // Fallback to direct navigation
      navigate('/kanban-board', {
        state: {
          projectId: currentProject.id,
          project: currentProject,
        },
      });
    }
  };

  const handleAddTask = () => {
    if (!currentProject) return;
    const returnPath = location.pathname + location.search;
    navigate(`/card-details?mode=create&projectId=${currentProject.id}`, {
      state: {
        project: currentProject,
        projectId: currentProject.id,
        returnPath: returnPath,
      },
    });
  };


  const handleInviteMembers = () => {
    setIsInviteModalOpen(true);
  };

  // Handler for Owner (bulk invite)
  const handleOwnerInviteSubmit = async (inviteData) => {
    const orgId = currentOrganization?.id || sessionService.getOrganizationId();
    if (!orgId) {
      console.error('No organization found');
      return;
    }

    try {
      console.log('Owner inviting members:', inviteData);

      for (const email of inviteData.emails) {
        await teamService.inviteTeamMember(orgId, {
          email,
          role: inviteData.role,
          welcomeMessage: inviteData.welcomeMessage,
        });
      }

      console.log('Invitations sent successfully');
      setIsInviteModalOpen(false);
      // Refresh project/team data
      await loadProjectData();
    } catch (error) {
      console.error('Failed to invite members:', error);
    }
  };

  // Handler for Admin (bulk invite)
  const handleAdminInviteSubmit = async (inviteData) => {
    const orgId = currentOrganization?.id || sessionService.getOrganizationId();
    if (!orgId) {
      console.error('No organization found');
      return;
    }

    try {
      console.log('Admin inviting members:', inviteData);

      for (const email of inviteData.emails) {
        await teamService.inviteTeamMember(orgId, {
          email,
          role: inviteData.role,
          welcomeMessage: inviteData.welcomeMessage,
        });
      }

      console.log('Invitations sent successfully');
      setIsInviteModalOpen(false);
      // Refresh project/team data
      await loadProjectData();
    } catch (error) {
      console.error('Failed to invite members:', error);
    }
  };

  const getStatusColor = (status) => {
    if (!status) return 'bg-muted text-text-secondary';

    switch (status.toLowerCase()) {
      case 'good':
        return 'bg-success/10 text-success';
      case 'at risk':
        return 'bg-warning/10 text-warning';
      case 'delayed':
        return 'bg-destructive/10 text-destructive';
      default:
        return 'bg-muted text-text-secondary';
    }
  };

  const getPriorityColor = (priority) => {
    if (!priority) return 'bg-muted text-text-secondary';

    switch (priority.toLowerCase()) {
      case 'high priority':
        return 'bg-destructive/10 text-destructive';
      case 'medium priority':
        return 'bg-warning/10 text-warning';
      case 'low priority':
        return 'bg-success/10 text-success';
      default:
        return 'bg-muted text-text-secondary';
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className='min-h-screen bg-background'>
        <RoleBasedHeader currentUser={user} userRole={userRole} />
        <main className='pt-16'>
          <div className='max-w-7xl mx-auto p-6'>
            <div className='flex items-center justify-center h-64'>
              <div className='text-center'>
                <Icon
                  name='Loader2'
                  size={32}
                  className='animate-spin mx-auto mb-4 text-primary'
                />
                <p className='text-text-secondary'>Loading project data...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className='min-h-screen bg-background'>
        <RoleBasedHeader currentUser={user} userRole={userRole} />
        <main className='pt-16'>
          <div className='max-w-7xl mx-auto p-6'>
            <div className='flex items-center justify-center h-64'>
              <div className='text-center'>
                <Icon
                  name='AlertCircle'
                  size={32}
                  className='mx-auto mb-4 text-destructive'
                />
                <h3 className='text-lg font-medium text-text-primary mb-2'>
                  Error Loading Project
                </h3>
                <p className='text-text-secondary mb-4'>{error}</p>
                <Button onClick={() => window.location.reload()}>
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Show main content when project data is loaded
  if (!currentProject) {
    return (
      <div className='min-h-screen bg-background'>
        <RoleBasedHeader currentUser={user} userRole={userRole} />
        <main className='pt-16'>
          <div className='max-w-7xl mx-auto p-6'>
            <div className='flex items-center justify-center h-64'>
              <div className='text-center max-w-md'>
                <Icon
                  name='FolderX'
                  size={48}
                  className='mx-auto mb-4 text-text-secondary'
                />
                <h3 className='text-xl font-semibold text-text-primary mb-2'>
                  Project Not Found
                </h3>
                <p className='text-text-secondary mb-6'>
                  {error || 'The requested project could not be found or you may not have permission to view it.'}
                </p>
                <div className='space-y-3'>
                  <Button onClick={() => navigate('/role-based-dashboard')} className='w-full'>
                    <Icon name='ArrowLeft' size={16} className='mr-2' />
                    Back to Dashboard
                  </Button>
                  <Button
                    variant='outline'
                    onClick={() => navigate('/project-management')}
                    className='w-full'
                  >
                    <Icon name='FolderPlus' size={16} className='mr-2' />
                    View All Projects
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Final safety check - if currentProject is still null, show loading
  if (!currentProject) {
    return (
      <div className='min-h-screen bg-background'>
        <RoleBasedHeader currentUser={user} userRole={userRole} />
        <main className='pt-16'>
          <div className='max-w-7xl mx-auto p-6'>
            <div className='flex items-center justify-center h-64'>
              <div className='text-center'>
                <Icon
                  name='Loader2'
                  size={32}
                  className='animate-spin mx-auto mb-4 text-primary'
                />
                <p className='text-text-secondary'>Loading project data...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-background'>
      <RoleBasedHeader currentUser={user} userRole={userRole} />

      <div className='flex-1 flex flex-col'>
        {/* Breadcrumb */}
        <div className='border-b border-border bg-card'>
          <div className='px-6 py-4'>
            <Breadcrumb
              items={[
                { label: 'Projects', path: '/projects' },
                {
                  label: currentProject?.name || 'Project',
                  path: `/project-overview/${currentProject?.id || ''}`,
                },
              ]}
            />
          </div>
        </div>

        {/* Project Header */}
        <div className='border-b border-border bg-card'>
          <div className='px-6 py-6'>
            <div className='flex items-start justify-between'>
              <div className='flex-1'>
                <div className='flex items-center space-x-3 mb-2'>
                  <h1 className='text-2xl font-bold text-text-primary'>
                    {currentProject.name}
                  </h1>
                  <span
                    className={`px-2 py-1 rounded-md text-xs font-medium ${getStatusColor(
                      currentProject.status
                    )}`}
                  >
                    {currentProject.status}
                  </span>
                </div>
                <p className='text-text-secondary mb-4 max-w-3xl'>
                  {currentProject.description}
                </p>
                <div className='flex items-center space-x-6 text-sm'>
                  <div className='flex items-center space-x-2'>
                    <Icon
                      name='Calendar'
                      size={16}
                      className='text-text-secondary'
                    />
                    <span className='text-text-secondary'>
                      {currentProject.startDate} - {currentProject.endDate}
                    </span>
                  </div>
                  <div className='flex items-center space-x-2'>
                    <Icon
                      name='Flag'
                      size={16}
                      className='text-text-secondary'
                    />
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(
                        currentProject.priority
                      )}`}
                    >
                      {currentProject.priority}
                    </span>
                  </div>
                </div>
              </div>
              <div className='flex items-center space-x-3'>
                <Button
                  variant='outline'
                  onClick={() => navigate('/project-management')}
                  iconName='Settings'
                  iconPosition='left'
                >
                  Settings
                </Button>
                <Button
                  variant='default'
                  onClick={handleAddTask}
                  iconName='Plus'
                  iconPosition='left'
                >
                  Add Task
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className='border-b border-border bg-card'>
          <div className='px-6'>
            <nav className='flex space-x-8'>
              {visibleTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center space-x-2 px-1 py-4 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-text-secondary hover:text-text-primary hover:border-border'
                  }`}
                >
                  <Icon name={tab.icon} size={16} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className='flex-1 p-6'>
          {activeTab === 'overview' && (
            <div className='space-y-6'>
              {/* Progress and Team Cards */}
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                {/* Progress Card */}
                <div className='bg-card border border-border rounded-lg p-6'>
                  <div className='flex items-center justify-between mb-4'>
                    <h3 className='text-lg font-semibold text-text-primary flex items-center'>
                      <Icon
                        name='TrendingUp'
                        size={20}
                        className='mr-2 text-primary'
                      />
                      Progress
                    </h3>
                  </div>
                  <div className='space-y-4'>
                    <div>
                      <div className='flex items-center justify-between mb-2'>
                        <span className='text-3xl font-bold text-text-primary'>
                          {currentProject.progress % 1 === 0 ? currentProject.progress : parseFloat(currentProject.progress).toFixed(2)}%
                        </span>
                        <span className='text-sm text-success font-medium'>
                          {currentProject.progressChange}
                        </span>
                      </div>
                      <div className='w-full bg-muted rounded-full h-2'>
                        <div
                          className='bg-primary h-2 rounded-full transition-all duration-300'
                          style={{ width: `${currentProject.progress}%` }}
                        ></div>
                      </div>
                      <p className='text-sm text-text-secondary mt-2'>
                        On track for completion
                      </p>
                    </div>
                  </div>
                </div>

                {/* Project Timeline Card */}
                <div className='bg-card border border-border rounded-lg p-6'>
                  <div className='flex items-center justify-between mb-4'>
                    <h3 className='text-lg font-semibold text-text-primary flex items-center'>
                      <Icon
                        name='Calendar'
                        size={20}
                        className='mr-2 text-primary'
                      />
                      Timeline
                    </h3>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      (() => {
                        const start = new Date(currentProject.startDate);
                        const end = new Date(currentProject.endDate);
                        const today = new Date();
                        const total = end - start;
                        const elapsed = today - start;
                        const timePercent = Math.min(100, Math.max(0, (elapsed / total) * 100));
                        const progress = currentProject.progress || 0;
                        if (timePercent > progress + 15) return 'bg-destructive/10 text-destructive'; // High risk
                        if (timePercent > progress + 5) return 'bg-warning/10 text-warning'; // Moderate risk
                        return 'bg-success/10 text-success'; // On track
                      })()
                    }`}>
                      {(() => {
                        const start = new Date(currentProject.startDate);
                        const end = new Date(currentProject.endDate);
                        const today = new Date();
                        const total = end - start;
                        const elapsed = today - start;
                        const timePercent = Math.min(100, Math.max(0, (elapsed / total) * 100));
                        const progress = currentProject.progress || 0;
                        if (timePercent > progress + 15) return 'At Risk';
                        if (timePercent > progress + 5) return 'Attention Needed';
                        return 'On Track';
                      })()}
                    </span>
                  </div>
                  <div className='space-y-4'>
                    {/* Days Remaining */}
                    <div className='text-center py-2'>
                        {(() => {
                            const end = new Date(currentProject.endDate);
                            const today = new Date();
                            const diffTime = end - today;
                            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                            return (
                                <>
                                    <span className='text-3xl font-bold text-text-primary'>{Math.max(0, diffDays)}</span>
                                    <p className='text-xs text-text-secondary uppercase tracking-wider font-semibold mt-1'>Days Remaining</p>
                                </>
                            );
                        })()}
                    </div>

                    {/* Comparisons */}
                    <div className='space-y-3 pt-2'>
                        {/* Time Elapsed */}
                        <div>
                            <div className='flex justify-between text-xs mb-1'>
                                <span className='text-text-secondary'>Time Elapsed</span>
                                {(() => {
                                    const start = new Date(currentProject.startDate);
                                    const end = new Date(currentProject.endDate);
                                    const today = new Date();
                                    const total = end - start;
                                    const elapsed = today - start;
                                    const percent = Math.min(100, Math.max(0, (elapsed / total) * 100));
                                    return <span className='font-medium'>{percent % 1 === 0 ? percent : percent.toFixed(2)}%</span>
                                })()}
                            </div>
                            <div className='w-full bg-muted rounded-full h-1.5'>
                                {(() => {
                                    const start = new Date(currentProject.startDate);
                                    const end = new Date(currentProject.endDate);
                                    const today = new Date();
                                    const total = end - start;
                                    const elapsed = today - start;
                                    const percent = Math.min(100, Math.max(0, (elapsed / total) * 100));
                                    return (
                                        <div
                                            className='bg-text-secondary h-1.5 rounded-full opacity-50'
                                            style={{ width: `${percent}%` }}
                                        ></div>
                                    )
                                })()}
                            </div>
                        </div>

                         {/* Work Completed */}
                         <div>
                            <div className='flex justify-between text-xs mb-1'>
                                <span className='text-text-secondary'>Work Completed</span>
                                <span className='font-medium'>{currentProject.progress % 1 === 0 ? currentProject.progress : parseFloat(currentProject.progress).toFixed(2)}%</span>
                            </div>
                            <div className='w-full bg-muted rounded-full h-1.5'>
                                <div
                                    className='bg-primary h-1.5 rounded-full'
                                    style={{ width: `${currentProject.progress}%` }}
                                ></div>
                            </div>
                        </div>
                    </div>
                  </div>
                </div>

                {/* Team Workload Card */}
                <div className='bg-card border border-border rounded-lg p-6'>
                  <div className='flex items-center justify-between mb-4'>
                    <h3 className='text-lg font-semibold text-text-primary flex items-center'>
                      <Icon
                        name='Users'
                        size={20}
                        className='mr-2 text-primary'
                      />
                      Team Workload
                    </h3>
                  </div>
                  <div className='space-y-4'>
                    {/* Active Stats */}
                    <div className='flex justify-between items-center text-sm pb-2 border-b border-border'>
                       <span className='text-text-secondary'>Total Members:</span>
                       <span className='font-semibold'>{currentProject.team.totalMembers}</span>
                    </div>

                    {/* Member List */}
                    <div className='space-y-4 max-h-[160px] overflow-y-auto pr-1'>
                        {teamMembers && teamMembers.length > 0 ? (
                            teamMembers.slice(0, 3).map((member, idx) => (
                                <div key={idx} className='flex items-center space-x-3'>
                                    <div className='w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-bold'>
                                        {member.name ? member.name.charAt(0).toUpperCase() : 'U'}
                                    </div>
                                    <div className='flex-1'>
                                        <div className='flex justify-between text-xs mb-1'>
                                            <span className='font-medium text-text-primary truncate max-w-[80px]'>{member.name}</span>
                                            <span className='text-text-secondary'>{member.tasksCompleted || 0}/{member.tasksAssigned || 0} tasks</span>
                                        </div>
                                        <div className='w-full bg-muted rounded-full h-1.5'>
                                            <div
                                                className='bg-primary h-1.5 rounded-full'
                                                style={{ width: `${member.tasksAssigned ? Math.min(100, ((member.tasksCompleted || 0) / member.tasksAssigned) * 100) : 0}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className='text-center py-4 text-sm text-text-secondary'>
                                No active members
                            </div>
                        )}
                    </div>

                    {(userRole === 'owner' || userRole === 'admin') && (
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={handleInviteMembers}
                        iconName='UserPlus'
                        iconPosition='left'
                        className='w-full mt-2'
                      >
                        Invite
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Team Members Section */}
              <div className='bg-card border border-border rounded-lg p-6'>
                <div className='flex items-center justify-between mb-6'>
                  <h3 className='text-lg font-semibold text-text-primary'>
                    Team Members
                  </h3>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => navigate('/team-members')}
                  >
                    View All
                  </Button>
                </div>
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
                  {teamMembers.length > 0 ? teamMembers.map((member) => (
                    <div
                      key={member.id}
                      className='flex items-center space-x-3 p-3 rounded-lg border border-border'
                    >
                      <img
                        src={member.avatar}
                        alt={member.name}
                        className='w-10 h-10 rounded-full object-cover'
                      />
                      <div className='flex-1 min-w-0'>
                        <p className='text-sm font-medium text-text-primary truncate'>
                          {member.name}
                        </p>
                        <p className='text-xs text-text-secondary truncate'>
                          {member.role}
                        </p>
                        <div className='flex items-center space-x-2 mt-1'>
                          <span
                            className={`w-2 h-2 rounded-full ${
                              member.status === 'active'
                                ? 'bg-success'
                                : 'bg-muted'
                            }`}
                          ></span>
                          <span className='text-xs text-text-secondary'>
                            {member.tasksCompleted}/{member.tasksAssigned} tasks
                          </span>
                        </div>
                      </div>
                    </div>
                  )) : (
                    <div className='col-span-full text-center py-8'>
                      <div className='text-muted-foreground'>
                        <p className='text-sm'>No team members assigned to this project</p>
                        <p className='text-xs mt-1'>Team members will appear here once assigned to the project</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Actions */}
              <div className='bg-card border border-border rounded-lg p-6'>
                <h3 className='text-lg font-semibold text-text-primary mb-4'>
                  Quick Actions
                </h3>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                  <Button
                    variant='outline'
                    onClick={handleGoToBoard}
                    iconName='Kanban'
                    iconPosition='left'
                    className='justify-start'
                  >
                    Go to Board
                  </Button>
                  <Button
                    variant='outline'
                    onClick={handleAddTask}
                    iconName='Plus'
                    iconPosition='left'
                    className='justify-start'
                  >
                    Add New Task
                  </Button>
                  <Button
                    variant='outline'
                    onClick={() => navigate('/team-members')}
                    iconName='Users'
                    iconPosition='left'
                    className='justify-start'
                  >
                    Manage Team
                  </Button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'tasks' && (
            <div className='text-center py-12'>
              <Icon
                name='CheckSquare'
                size={48}
                className='mx-auto text-text-secondary mb-4'
              />
              <h3 className='text-lg font-semibold text-text-primary mb-2'>
                Task Management
              </h3>
              <p className='text-text-secondary mb-6'>
                Manage and track project tasks
              </p>
              <Button
                variant='default'
                onClick={handleGoToBoard}
                iconName='Kanban'
                iconPosition='left'
              >
                Go to Kanban Board
              </Button>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className='text-center py-12'>
              <Icon
                name='Settings'
                size={48}
                className='mx-auto text-text-secondary mb-4'
              />
              <h3 className='text-lg font-semibold text-text-primary mb-2'>
                Project Settings
              </h3>
              <p className='text-text-secondary mb-6'>
                Configure project settings and permissions
              </p>
              <Button
                variant='default'
                onClick={() => navigate('/project-management')}
                iconName='Settings'
                iconPosition='left'
              >
                Open Settings
              </Button>
            </div>
          )}
        </div>
      </div>

      <InviteMemberModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onInvite={userRole === 'owner' ? handleOwnerInviteSubmit : handleAdminInviteSubmit}
        organizationId={currentOrganization?.id || sessionService.getOrganizationId()}
        existingMemberEmails={teamMembers.map(m => m.email)}
      />

    </div>
  );
};

export default ProjectOverview;
